import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { BLOG_ARTICLES } from '../data/blog/articles';
import { sanitizeHtml } from '../lib/sanitizeDisplay';

/** Converts Markdown-like content (## headings, ** bold, paragraphs) to HTML.
 *  RT-20: All user-visible text is HTML-escaped before insertion.
 */
function renderMarkdown(text: string): string {
  return text
    .split('\n\n')
    .map(block => {
      if (block.startsWith('## '))  return `<h2>${sanitizeHtml(block.slice(3))}</h2>`;
      if (block.startsWith('### ')) return `<h3>${sanitizeHtml(block.slice(4))}</h3>`;
      // Bold, escape first, then restore <strong> tags
      const escaped = sanitizeHtml(block);
      const p = escaped.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      return `<p>${p}</p>`;
    })
    .join('');
}

export function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const article = BLOG_ARTICLES.find(a => a.slug === slug);

  if (!article) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-base)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: 'var(--text-muted)' }}>Artigo não encontrado.</p>
          <Link to="/blog" style={{ color: 'var(--petrol)', marginTop: '1rem', display: 'inline-block' }}>← Ver todos os artigos</Link>
        </div>
      </div>
    );
  }

  const ogImage = `https://professya.com/og/blog-${article.slug}.png`;
  const articleUrl = `https://professya.com/blog/${article.slug}`;

  return (
    <>
      <Helmet>
        <title>{article.title}, Professya</title>
        <meta name="description" content={article.metaDescription} />
        <meta property="og:title" content={article.ogTitle} />
        <meta property="og:description" content={article.metaDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={articleUrl} />
        <meta property="og:type" content="article" />
        <meta property="article:published_time" content={article.publishedAt} />
        <meta property="article:modified_time" content={article.updatedAt} />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={articleUrl} />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          description: article.metaDescription,
          image: ogImage,
          datePublished: article.publishedAt,
          dateModified: article.updatedAt,
          author: { '@type': 'Organization', name: 'Professya', url: 'https://professya.com' },
          publisher: { '@type': 'Organization', name: 'Professya', logo: { '@type': 'ImageObject', url: 'https://professya.com/logo.png' }, url: 'https://professya.com' },
          url: articleUrl,
          keywords: article.tags?.join(', ') ?? 'astrologia, mapa natal, esotérico',
          mainEntityOfPage: { '@type': 'WebPage', '@id': articleUrl },
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Início', item: 'https://professya.com' },
            { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://professya.com/blog' },
            { '@type': 'ListItem', position: 3, name: article.title, item: articleUrl },
          ],
        })}</script>
      </Helmet>

      <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-base)', color: 'var(--text-primary)' }}>
        {/* Header */}
        <div style={{ background: 'linear-gradient(180deg, rgba(245,200,66,0.06) 0%, transparent 100%)', padding: '3rem 1.5rem 2rem' }}>
          <div style={{ maxWidth: '720px', margin: '0 auto' }}>
            <Link to="/blog" style={{ color: 'var(--petrol)', textDecoration: 'none', fontSize: 'var(--text-caption)', display: 'inline-block', marginBottom: '1.5rem' }}>
              ← Blog Professya
            </Link>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
              {article.tags.map(tag => (
                <span key={tag} style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', backgroundColor: 'rgba(245,200,66,0.1)', padding: '0.2rem 0.6rem', borderRadius: 'var(--radius-full)', border: '1px solid rgba(245,200,66,0.2)' }}>
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="font-display" style={{ fontSize: 'clamp(1.5rem,4vw,2.25rem)', lineHeight: 1.2, marginBottom: '0.75rem' }}>
              {article.title}
            </h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-body)', marginBottom: '1rem', lineHeight: 1.6 }}>
              {article.subtitle}
            </p>
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>
              {new Date(article.publishedAt).toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })}
              {' · '}
              {article.readingTimeMinutes} min de leitura
            </p>
          </div>
        </div>

        {/* Content */}
        <div style={{ maxWidth: '720px', margin: '0 auto', padding: '0 1.5rem 4rem' }}>
          <div
            className="blog-content"
            style={{ color: 'var(--text-secondary)', lineHeight: 1.85, fontSize: 'var(--text-body)' }}
            dangerouslySetInnerHTML={{ __html: renderMarkdown(article.content) }}
          />

          {/* CTA */}
          <div className="card" style={{ marginTop: '3rem', textAlign: 'center', border: '1px solid rgba(245,200,66,0.2)', background: 'linear-gradient(135deg, rgba(245,200,66,0.06) 0%, transparent 100%)' }}>
            <p className="font-display" style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>
              ✦ Professya, Astrologia Personalizada
            </p>
            <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', marginBottom: '1.25rem' }}>
              Aplique o que você aprendeu. Crie sua conta gratuitamente e acesse seu mapa natal completo, tarô e previsões personalizadas.
            </p>
            <Link to="/register" className="btn btn-primary">Criar conta grátis →</Link>
          </div>

          {/* Related articles */}
          {BLOG_ARTICLES.filter(a => a.slug !== article.slug).length > 0 && (
            <div style={{ marginTop: '2.5rem' }}>
              <h3 className="font-display" style={{ fontSize: '1.125rem', marginBottom: '1rem' }}>Artigos relacionados</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {BLOG_ARTICLES.filter(a => a.slug !== article.slug).slice(0, 3).map(a => (
                  <Link key={a.slug} to={`/blog/${a.slug}`}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.875rem 1rem', backgroundColor: 'var(--bg-card)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)', textDecoration: 'none', color: 'var(--text-primary)' }}>
                    <div>
                      <p style={{ fontWeight: 600, fontSize: 'var(--text-label)', margin: 0 }}>{a.title}</p>
                      <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: '0.2rem 0 0' }}>{a.readingTimeMinutes} min de leitura</p>
                    </div>
                    <span style={{ color: 'var(--petrol)', fontSize: '1.125rem' }}>→</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
